import React, { createElement, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SpotlightActionData, SpotlightActionGroupData, spotlight } from '@mantine/spotlight';
import {
  LayoutDashboard,
  Calendar,
  PlusCircle,
  Users,
  UserPlus,
  CreditCard,
  BarChart3,
  Compass,
  Sparkles,
  Wallet,
  PenSquare,
  Building,
  Layers,
  Ticket,
  CheckSquare,
  Heart,
  Percent,
  Mic,
  Coins,
  Receipt,
  UsersRound,
  MessageSquare,
  CalendarRange,
  LogOut,
  Palette,
  User,
  CalendarCheck,
  Megaphone,
  LucideIcon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNightMode } from '../contexts/NightModeContext';
import {
  getAgendaByOrganizer,
  getAllEventsByOrganizerId,
  getAllTicketGroupBySlug,
  getBookingSlot,
  getEventListByOrganizer,
  getLineupsByOrganizer,
} from '../api';
import {
  getCommonNavItems,
  getEventNavItems,
  getOrganizerNavContext,
  getQuickActionItems,
  getVenueEventNavItems,
  getWorkspaceNavItems,
  OrganizerNavItem,
} from '../navigation/organizerNavConfig';

const ICON_SIZE = 18;

/** Icon key → lucide component map (no JSX — safe in a .ts file). */
const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard,
  Calendar,
  PlusCircle,
  Users,
  UserPlus,
  CreditCard,
  BarChart3,
  Compass,
  Sparkles,
  Wallet,
  PenSquare,
  Building,
  Layers,
  Ticket,
  CheckSquare,
  Heart,
  Percent,
  Mic,
  Coins,
  Receipt,
  UsersRound,
  MessageSquare,
  CalendarRange,
  LogOut,
  Palette,
  User,
  CalendarCheck,
  Megaphone,
};

/** Map icon key strings from nav config to lucide React nodes. */
function renderNavIcon(iconKey?: string): React.ReactNode {
  if (!iconKey) return null;
  const Icon = ICON_MAP[iconKey];
  if (!Icon) return null;
  return createElement(Icon, { size: ICON_SIZE });
}

function readAuthRoles() {
  let isEventSecondAdmin = false;
  let isCoOrganizer = false;
  let organizerId: number | null = null;
  let userEventIds: any[] = [];
  let userRole = '';
  try {
    const authUser = localStorage.getItem('authUser');
    if (authUser) {
      const parsedData = JSON.parse(authUser);
      const role = String(parsedData.user?.userRole ?? '');
      userRole = role;
      // Match SideNavMenu: role 8 = event second admin, role 10 = co-organizer
      isEventSecondAdmin = role === '8';
      isCoOrganizer = role === '10';
      organizerId = parsedData.user?.organizerId
        ? Number(parsedData.user.organizerId)
        : null;
      userEventIds = parsedData.user?.eventId || [];
    }
  } catch (err) {
    console.error('Error parsing authUser for spotlight', err);
  }
  return { isEventSecondAdmin, isCoOrganizer, organizerId, userEventIds, userRole };
}

function readEventDataFromStorage() {
  try {
    const raw = localStorage.getItem('eventData');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.error('Invalid eventData in localStorage', err);
    return null;
  }
}

/**
 * Builds Spotlight actions: sidebar nav + searchable events + quick actions.
 * Typing an event name filters the Events group via Spotlight's built-in filter.
 */
export function useOrganizerSpotlightActions(): (SpotlightActionGroupData | SpotlightActionData)[] {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { user } = useAuth();
  const { isNightMode, toggleNightMode } = useNightMode();

  const entitlements = user?.entitlements || [];
  const isPayoutEnabled = entitlements.length > 0 && entitlements.includes('payout');
  const authRoles = useMemo(() => readAuthRoles(), []);
  const { isEventSecondAdmin, isCoOrganizer, organizerId, userEventIds, userRole } = authRoles;
  const canCreateEvent = !isEventSecondAdmin && !isCoOrganizer;

  const navContext = getOrganizerNavContext(location.pathname);

  const routeEventId = params.eventId;
  const storedEvent = useMemo(() => {
    void location.pathname;
    return readEventDataFromStorage();
  }, [location.pathname]);
  const eventId = routeEventId || storedEvent?.eventId || '';
  const eventName = storedEvent?.eventName || 'Event';

  const [hasSlotData, setHasSlotData] = useState(false);
  const [hasAgendaData, setHasAgendaData] = useState(false);
  const [hasMultipleVenues, setHasMultipleVenues] = useState(false);
  const [hasMultipleTicketGroups, setHasMultipleTicketGroups] = useState(false);
  const [isEventClosed, setIsEventClosed] = useState(false);
  // Organizer events shown as searchable Spotlight rows
  const [organizerEvents, setOrganizerEvents] = useState<any[]>([]);

  useEffect(() => {
    const data = readEventDataFromStorage();
    if (!data) {
      setHasMultipleVenues(false);
      setHasMultipleTicketGroups(false);
      setIsEventClosed(false);
      return;
    }
    setHasMultipleVenues(!!data.hasMultipleVenues);
    setHasMultipleTicketGroups(!!data.hasMultipleTicketGroups);
    setIsEventClosed(data.eventStatus === 'closed');
  }, [location.pathname, eventId]);

  // Load all organizer events so Spotlight can search them by name/venue
  useEffect(() => {
    let cancelled = false;

    const loadEvents = async () => {
      try {
        let list: any[] = [];
        if (isEventSecondAdmin && organizerId) {
          const res = await getAllEventsByOrganizerId(organizerId);
          list = res?.events || [];
          // Event second admins (role 8) only see assigned events; co-organizers see all
          if (userRole === '8') {
            list = list.filter((e: any) =>
              userEventIds.map(String).includes(String(e.eventId))
            );
          }
        } else if (userRole === '10' && organizerId) {
          // Co-organizer also loads via organizer id list
          const res = await getAllEventsByOrganizerId(organizerId);
          list = res?.events || [];
        } else {
          const res = await getEventListByOrganizer();
          list = res?.data?.events || res?.events || [];
        }
        if (!cancelled) setOrganizerEvents(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Failed to load events for spotlight', err);
        if (!cancelled) setOrganizerEvents([]);
      }
    };

    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [isEventSecondAdmin, organizerId, userRole, userEventIds]);

  useEffect(() => {
    if (navContext === 'common') {
      setHasSlotData(false);
      setHasAgendaData(false);
      return;
    }

    let cancelled = false;

    const loadFlags = async () => {
      const data = readEventDataFromStorage();
      const slugId = data?.slugId;
      if (!slugId) {
        if (!cancelled) {
          setHasSlotData(false);
          setHasAgendaData(false);
        }
        return;
      }

      try {
        if (navContext === 'event') {
          const [lineups, agenda] = await Promise.all([
            getLineupsByOrganizer().catch(() => []),
            getAgendaByOrganizer().catch(() => []),
          ]);
          if (!cancelled) {
            setHasAgendaData(Array.isArray(agenda) && agenda.length > 0);
          }
          void lineups;
        } else if (!cancelled) {
          setHasAgendaData(false);
        }

        const [slotData, ticketGroups] = await Promise.all([
          getBookingSlot(slugId).catch(() => []),
          getAllTicketGroupBySlug(slugId).catch(() => []),
        ]);

        const hasSlotRequiredGroup =
          Array.isArray(ticketGroups) &&
          ticketGroups.some(
            (group: any) => group.isSlotRequired === true || group.isSlotRequired === 'true'
          );

        if (!cancelled) {
          setHasSlotData((Array.isArray(slotData) && slotData.length > 0) || hasSlotRequiredGroup);
        }
      } catch (err) {
        console.error('Failed to load spotlight event flags', err);
        if (!cancelled) {
          setHasSlotData(false);
          setHasAgendaData(false);
        }
      }
    };

    loadFlags();
    return () => {
      cancelled = true;
    };
  }, [navContext, eventId, location.pathname]);

  const runNavItem = (item: OrganizerNavItem) => {
    spotlight.close();

    if (item.actionKey === 'theme') {
      toggleNightMode();
      return;
    }

    if (item.actionKey === 'logout') {
      localStorage.clear();
      window.location.href = '/login';
      return;
    }

    if (item.path) {
      navigate(item.path);
    }
  };

  const openOrganizerEvent = (evt: any) => {
    spotlight.close();
    localStorage.setItem('eventData', JSON.stringify(evt));
    const isDraft = evt.eventStatus === 'draft';
    if (isDraft) {
      navigate(`/events/${evt.eventId}/preview`);
    } else {
      navigate(`/event/${evt.eventId}`);
    }
  };

  const toSpotlightAction = (item: OrganizerNavItem): SpotlightActionData => ({
    id: item.id,
    label: item.label,
    description: item.description,
    keywords: item.keywords,
    leftSection: renderNavIcon(item.iconKey),
    onClick: () => runNavItem(item),
  });

  return useMemo(() => {
    let navItems: OrganizerNavItem[] = [];

    if (navContext === 'common') {
      navItems = getCommonNavItems({
        isEventSecondAdmin,
        isCoOrganizer,
        isPayoutEnabled,
      });
    } else if (navContext === 'event' && eventId) {
      navItems = getEventNavItems({
        eventId,
        eventName,
        hasMultipleVenues,
        hasMultipleTicketGroups,
        hasAgendaData,
        hasSlotData,
        isEventClosed,
      });
    } else if (navContext === 'venue-event' && eventId) {
      navItems = getVenueEventNavItems({
        eventId,
        eventName,
        hasSlotData,
      });
    } else {
      navItems = getCommonNavItems({
        isEventSecondAdmin,
        isCoOrganizer,
        isPayoutEnabled,
      });
    }

    const quickItems = getQuickActionItems({ canCreateEvent, isNightMode });

    // Events group — searchable by name, venue, status, city
    const eventActions: SpotlightActionData[] = organizerEvents.map((evt) => {
      const status = evt.eventStatus || 'unknown';
      const venue = evt.eventVenue || evt.venueName || '';
      const city = evt.eventCity || '';
      return {
        id: `event-${evt.eventId}`,
        label: evt.eventName || `Event ${evt.eventId}`,
        description: [venue, city, status].filter(Boolean).join(' · '),
        keywords: [evt.eventName, venue, city, status, String(evt.eventId)].filter(
          Boolean
        ) as string[],
        leftSection: renderNavIcon('CalendarCheck'),
        onClick: () => openOrganizerEvent(evt),
      };
    });

    const groups: (SpotlightActionGroupData | SpotlightActionData)[] = [];

    // On an event page: show THIS event's sidebar tools first (Dashboard, Tickets, …)
    if (navContext === 'event' || navContext === 'venue-event') {
      groups.push({
        group: navContext === 'event' ? 'This event' : 'This venue event',
        actions: navItems.map(toSpotlightAction),
      });
      // Also jump back to organizer home / all events
      groups.push({
        group: 'Workspace',
        actions: getWorkspaceNavItems().map(toSpotlightAction),
      });
    } else {
      groups.push({
        group: 'Pages',
        actions: navItems.map(toSpotlightAction),
      });
    }

    if (eventActions.length > 0) {
      groups.push({
        group: 'Events',
        actions: eventActions,
      });
    }

    groups.push({
      group: 'Quick actions',
      actions: quickItems.map(toSpotlightAction),
    });

    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navContext,
    eventId,
    eventName,
    hasMultipleVenues,
    hasMultipleTicketGroups,
    hasAgendaData,
    hasSlotData,
    isEventClosed,
    isEventSecondAdmin,
    isCoOrganizer,
    isPayoutEnabled,
    canCreateEvent,
    isNightMode,
    organizerEvents,
    navigate,
    toggleNightMode,
  ]);
}
